/* DreamPage — LIST of dream vacations. v10.5 redesign.
 *
 * Each existing dream renders as a full-image card with the
 * destination cover as background, an overlay gradient, and the
 * title + date/status pill in the bottom-left. Below the last card
 * is an "Add more" cream-dashed tile that opens the new-dream flow.
 * Zero backend changes — reads the same `/api/dream/vacations`.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Star, Sparkles, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SectionRibbon } from "@/components/AppShell";
import { DREAM } from "@/constants/testIds";
// Phase 5 (2026-07-28): GooglePlacesPhoto retired with Google
// Places. Dream vacations previously stored `photo_names` from
// Google — those are nulled by the wipe migration. Covers now come
// from `cover_image_url` (Unsplash / user upload / Hotelbeds) or
// the app-wide navy-gradient placeholder.
import { toast } from "sonner";

const FALLBACK_DREAM_IMG =
  "https://images.unsplash.com/photo-1533105079780-92b9be482077?auto=format&fit=crop&w=1600&q=80";

// Session-cache for cover-image refetches (2026-07 FIX 3 — Google
// Places / CruiseDirect bookmarks store the id but no cover URL on
// the dream vacation; we refetch live so the card doesn't render as
// the generic Unsplash fallback). Keyed on destination_id, hits
// `/api/cards/{id}/public` which the Bookmarks page already uses.
const _dreamCoverCache = new Map();

/** Returns a resolved cover URL for a dream vacation. When the
 *  vacation already ships `cover_image_url`, it's returned verbatim.
 *  Otherwise, for Google Places / CruiseDirect destinations we fetch
 *  the public card once per session and pull `images[0]`. */
function useDreamCover(v) {
  const shipped = v?.cover_image_url;
  const destId = v?.destination_id || "";
  // Phase 5 · final cleanup (2026-07-28): dropped the legacy
  // `google_places:` prefix check — the wipe migration cleared every
  // dream vacation carrying a Google id. CruiseDirect is the only
  // remaining provider that stores an id without a cover URL.
  const needsFetch = !shipped && destId.startsWith("cruisedirect:");
  const cached = _dreamCoverCache.get(destId);
  const [url, setUrl] = React.useState(shipped || cached || null);

  React.useEffect(() => {
    if (!needsFetch || cached) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/cards/${encodeURIComponent(destId)}/public`);
        const cover = (data?.images && data.images[0]) || null;
        if (!cover) return;
        _dreamCoverCache.set(destId, cover);
        if (!cancelled) setUrl(cover);
      } catch { /* silent — falls through to FALLBACK_DREAM_IMG */ }
    })();
    return () => { cancelled = true; };
  }, [destId, needsFetch, cached]);

  return url || FALLBACK_DREAM_IMG;
}

function fmtDreamMeta(v) {
  const pct = v.goal_amount > 0
    ? Math.min(100, Math.round(((v.saved_amount || 0) / v.goal_amount) * 100))
    : 0;
  if (pct >= 100) return { label: "Ready to book", tone: "gold" };
  if (pct > 0)    return { label: "In Progress",  tone: "gold" };
  return { label: "Planned", tone: "navy" };
}

function DreamCard({ v, onClick, onDelete }) {
  const meta = fmtDreamMeta(v);
  const img = useDreamCover(v);
  // Phase 5 (2026-07-28): removed the Google-Places photo branch
  // — dream vacations no longer carry Google `photo_names`.
  const pillCls = meta.tone === "gold"
    ? "bg-white/15 border border-tb-gold text-tb-gold-bright"
    : "bg-white/15 border border-white/50 text-white";
  return (
    <div className="relative w-full group">
      <button
        data-testid={DREAM.card(v.id)}
        onClick={onClick}
        className="relative w-full text-left rounded-2xl overflow-hidden aspect-[16/9] shadow-md hover:shadow-xl transition tb-tap"
      >
        <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-tb-navy-deep/85 via-tb-navy-deep/20 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-5 md:p-6 pr-14">
          <h3 className="font-serif-it italic text-white text-2xl md:text-3xl leading-tight drop-shadow-lg line-clamp-1">
            {v.title || "Untitled dream"}{v.country ? `, ${v.country}` : ""}
          </h3>
          <div className="mt-2 flex items-center gap-2">
            <span className={`inline-flex px-3 py-1 rounded-full font-hand text-xs uppercase tracking-wider ${pillCls}`}>
              {meta.label}
            </span>
            {v.goal_amount > 0 && (
              <span className="font-hand text-white/85 text-xs">
                ${Math.round(v.saved_amount || 0).toLocaleString()} / ${Math.round(v.goal_amount).toLocaleString()}
              </span>
            )}
          </div>
        </div>
      </button>
      {/* Item #35 — delete button. Ghost-style so it sits quietly on the
          hero image but stays reachable on both hover (desktop) and
          touch (mobile always visible at 55% opacity). Stops propagation
          so tapping it doesn't also navigate into the vacation. */}
      <button
        data-testid={DREAM.cardDeleteBtn(v.id)}
        onClick={(e) => { e.stopPropagation(); onDelete(v); }}
        aria-label={`Delete ${v.title || "dream vacation"}`}
        className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm text-white/85 hover:text-tb-coral hover:bg-black/60 flex items-center justify-center tb-tap opacity-55 group-hover:opacity-100 transition-opacity"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

/* Item #35 — shared confirmation modal for deleting a dream vacation.
   Used by both the list page and the detail page header. */
function DeleteConfirmDialog({ vacation, busy, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 bg-tb-navy-deep/70 flex items-end md:items-center justify-center p-4">
      <div data-testid={DREAM.deleteConfirmDialog} role="dialog" aria-modal="true"
        className="bg-tb-cream rounded-2xl border-2 border-tb-coral max-w-md w-full">
        <div className="bg-tb-coral text-white px-4 py-3 flex items-center justify-between rounded-t-2xl">
          <span className="font-hand text-xl">delete this dream?</span>
          <button type="button" onClick={onCancel} className="font-hand text-white/85 tb-tap">close</button>
        </div>
        <div className="p-5 space-y-4">
          <p className="font-serif-it italic text-tb-navy text-xl leading-snug">
            {vacation?.title || "Untitled dream"}
            {vacation?.country ? `, ${vacation.country}` : ""}
          </p>
          <p className="font-hand text-tb-navy/70 text-sm">
            This clears the money jar, the pin board and the itinerary you saved
            for this vacation. It can't be undone.
          </p>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button" onClick={onCancel} disabled={busy}
              data-testid={DREAM.deleteConfirmCancel}
              className="px-4 py-2 rounded-full font-hand text-sm text-tb-navy/70 tb-tap"
            >
              cancel
            </button>
            <button
              type="button" onClick={onConfirm} disabled={busy}
              data-testid={DREAM.deleteConfirmSubmit}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-tb-coral text-white border-2 border-tb-coral font-hand text-sm tb-tap disabled:opacity-60"
            >
              <Trash2 className="w-3.5 h-3.5" />
              {busy ? "deleting…" : "delete dream"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AddMoreCard({ onClick }) {
  return (
    <button
      data-testid="dream-add-more-card"
      onClick={onClick}
      className="w-full rounded-2xl border-2 border-dashed border-tb-cream-3 bg-tb-cream/60 hover:border-tb-gold hover:bg-tb-cream aspect-[16/9] flex flex-col items-center justify-center gap-2 tb-tap transition-colors"
    >
      <div className="w-14 h-14 rounded-full bg-tb-navy-deep flex items-center justify-center">
        <Plus className="w-7 h-7 text-tb-gold-bright" strokeWidth={2} />
      </div>
      <span className="font-serif-it italic text-tb-navy text-xl md:text-2xl">
        Add a new dream vacation
      </span>
    </button>
  );
}

function NewDreamDialog({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/dream/vacations", { title: title.trim() });
      onCreated(data.vacation);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't create dream");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-tb-navy-deep/70 flex items-end md:items-center justify-center p-4">
      <form onSubmit={submit} role="dialog" aria-modal="true"
        className="bg-tb-cream rounded-2xl border-2 border-tb-gold max-w-md w-full">
        <div className="bg-tb-navy text-tb-gold-bright px-4 py-3 flex items-center justify-between rounded-t-2xl">
          <span className="font-hand text-xl">start a new dream</span>
          <button type="button" onClick={onClose} className="font-hand text-tb-gold tb-tap">close</button>
        </div>
        <div className="p-4 space-y-3">
          <label className="block">
            <span className="font-hand text-tb-navy/65 text-sm">Where are you dreaming of?</span>
            <input
              data-testid={DREAM.newTitleInput}
              value={title} onChange={(e) => setTitle(e.target.value)} autoFocus
              placeholder="e.g. Santorini, Greece"
              className="w-full px-3 py-2 rounded-lg bg-tb-cream border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-base mt-1"
            />
          </label>
          <button type="submit" disabled={busy || !title.trim()}
            data-testid={DREAM.newSubmit}
            className="w-full py-2.5 rounded-xl bg-tb-navy text-tb-gold-bright border-2 border-tb-gold font-hand text-lg tb-tap disabled:opacity-60">
            {busy ? "creating\u2026" : "start dreaming"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function DreamPage() {
  const [vacations, setVacations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dream/vacations");
      setVacations(data.vacations || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load dreams");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/dream/vacations/${deleteTarget.id}`);
      toast.success("Dream vacation deleted");
      setVacations((prev) => prev.filter((v) => v.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't delete");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid={DREAM.listPage} className="flex flex-col bg-white">
      <SectionRibbon>your dream vacations</SectionRibbon>
      <div data-tour="dream-header" className="px-6 md:px-10 lg:px-12 pt-6 pb-10">
        <div className="flex items-center gap-3 mb-1">
          <Star className="w-6 h-6 text-tb-gold" fill="currentColor" strokeWidth={1.4} />
          <h1 className="font-serif-it italic text-tb-gold text-2xl md:text-3xl">
            build your dream vacation
          </h1>
        </div>
        <p className="font-hand text-tb-navy/55 text-sm md:text-base italic ml-9">
          a collection of destinations waiting for their perfect moment
        </p>

        <div className="mt-6">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="aspect-[16/9] rounded-2xl bg-tb-cream-2 border-2 border-tb-cream-3 animate-pulse" />
              ))}
            </div>
          ) : vacations.length === 0 ? (
            <div data-testid={DREAM.emptyState} className="text-center py-14 px-6 rounded-2xl bg-tb-cream border-2 border-tb-cream-3">
              <div className="mx-auto w-16 h-16 rounded-full bg-tb-navy-deep flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-tb-gold-bright" strokeWidth={1.8} />
              </div>
              {/* 2026-07-18 — the "Create your first dream" CTA on the
                  empty state was reported broken. Removed the new-dream
                  modal trigger here and redirected users to /explore
                  where dreams originate. The `+ New dream` tile keeps
                  working for users who already have 1+ dream. */}
              <p className="mt-4 font-serif-it italic text-tb-navy text-2xl md:text-3xl">No dreams yet</p>
              <p className="mt-2 font-hand text-tb-navy/60 text-sm md:text-base max-w-md mx-auto">
                Head to Explore and save destinations that catch your eye.
                <br />
                We&rsquo;ll build your dream vacations from there.
              </p>
              <button
                data-testid="dream-empty-explore-cta"
                onClick={() => nav("/explore")}
                className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-tb-navy-deep text-tb-gold-bright border-2 border-tb-gold font-hand text-base tb-tap hover:brightness-110"
              >
                Explore destinations
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 md:gap-6">
              {vacations.map((v) => (
                <DreamCard
                  key={v.id}
                  v={v}
                  onClick={() => nav(`/dream/vacations/${v.id}`)}
                  onDelete={setDeleteTarget}
                />
              ))}
              <AddMoreCard onClick={() => setShowNew(true)} />
            </div>
          )}
        </div>
      </div>

      {showNew && (
        <NewDreamDialog
          onClose={() => setShowNew(false)}
          onCreated={(v) => nav(`/dream/vacations/${v.id}`)}
        />
      )}
      {deleteTarget && (
        <DeleteConfirmDialog
          vacation={deleteTarget}
          busy={deleting}
          onCancel={() => (deleting ? null : setDeleteTarget(null))}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}
